// Remembers the Yoma link context a visitor ARRIVED with
// (`?yoma_ref=&yoma_opportunity=&yoma_country=`), so the run they start can be
// attributed to the Yoma opportunity they came from (#4044, Pull Sync Phase A).
//
// THIS IS LEARNER IDENTITY, NOT MARKETING ATTRIBUTION (plan check P1-5), so the
// lifecycle is stricter than entrySource.js (which it follows for boot capture):
//   1. CAPTURE   only on a `/scenarios/<numeric id>` landing; bound to that id.
//   2. INVALIDATE a landing carrying ANY yoma_* param that fails validation
//                CLEARS what is held (a new learner with a malformed link must
//                not inherit the previous learner's ref); a valid yoma_ref
//                replaces it; a landing with no yoma_* param keeps it (reload).
//   3. EXPIRE    ignored and cleared after 2 hours.
//   4. CONSUME   ScenarioDetailPage sends it once as `body.yomaEntry`, only for
//                the SAME scenario id, and clears it on a 201.
//   5. STRIP     the three yoma_* params leave the address bar at capture
//                (links get shared in WhatsApp groups: a shared ref would credit
//                the sharer). `lang` and every other param are untouched.
//   6. NEVER reaches entrySource.js (it reads only utm_*): tested.
//
// THE SERVER IS AUTHORITATIVE (backend/src/services/yoma/yomaPartnerEntry.js
// re-validates the wire object). This copy exists so a malformed value never
// reaches storage or the network. The rules below are a COPY; one shared case
// table (backend/src/__tests__/helpers/yomaEntryCases.js) drives both tests.
//
// DELIBERATELY NOT entrySource's credential redactor: a 64-hex reference is a
// legal Yoma ref and must be stored verbatim. The ref is an allowlist-validated
// opaque identifier. It is never logged.

const STORAGE_KEY = 'tacit_yoma_entry';
const VERSION = 1;
const TTL_MS = 2 * 60 * 60 * 1000;

const PARAM_REF = 'yoma_ref';
const PARAM_OPPORTUNITY = 'yoma_opportunity';
const PARAM_COUNTRY = 'yoma_country';
const YOMA_PARAMS = [PARAM_REF, PARAM_OPPORTUNITY, PARAM_COUNTRY];

const REF_PATTERN = /^[A-Za-z0-9._~-]{1,128}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const COUNTRY_PATTERN = /^[A-Za-z]{2}$/;
const SCENARIO_PATH = /^\/scenarios\/(\d+)\/?$/;

/**
 * COPY of backend parseYomaEntry. `raw` is `{ref, opportunity, country}`.
 * @returns {{status:'absent'|'invalid'|'valid', value:null|{ref:string, opportunityId:string, country:string|null}}}
 */
export function parseYomaEntry(raw) {
  if (raw === undefined || raw === null) return { status: 'absent', value: null };
  if (typeof raw !== 'object' || Array.isArray(raw)) return { status: 'invalid', value: null };
  const { ref, opportunity, country } = raw;
  if (typeof ref !== 'string' || !REF_PATTERN.test(ref)) return { status: 'invalid', value: null };
  if (typeof opportunity !== 'string' || !UUID_PATTERN.test(opportunity)) return { status: 'invalid', value: null };
  const countryOut = typeof country === 'string' && COUNTRY_PATTERN.test(country) ? country.toUpperCase() : null;
  return { status: 'valid', value: { ref, opportunityId: opportunity.toLowerCase(), country: countryOut } };
}

/** The three yoma_* values of a query string; `present` = any of the keys exists. */
export function readYomaParams(search) {
  let params;
  try {
    params = new URLSearchParams(String(search ?? ''));
  } catch {
    return { present: false, raw: {} };
  }
  const present = YOMA_PARAMS.some((k) => params.has(k));
  const raw = {};
  if (params.has(PARAM_REF)) raw.ref = params.get(PARAM_REF);
  if (params.has(PARAM_OPPORTUNITY)) raw.opportunity = params.get(PARAM_OPPORTUNITY);
  if (params.has(PARAM_COUNTRY)) raw.country = params.get(PARAM_COUNTRY);
  return { present, raw };
}

function storageOf(win) {
  try { return win.sessionStorage; } catch { return null; }
}
function storageRead(win = (typeof window !== 'undefined' ? window : undefined)) {
  try { return storageOf(win).getItem(STORAGE_KEY); } catch { return null; }
}
function storageWrite(value, win = (typeof window !== 'undefined' ? window : undefined)) {
  try { storageOf(win).setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* memory is primary */ }
}
function storageClear(win = (typeof window !== 'undefined' ? window : undefined)) {
  try { storageOf(win).removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

// PRIMARY SOURCE (sessionStorage can throw in Safari private mode).
let held = null;

// Storage is not trusted: re-validate every field on read.
function normaliseStored(rawJson) {
  if (typeof rawJson !== 'string') return null;
  let parsed;
  try { parsed = JSON.parse(rawJson); } catch { return null; }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  if (parsed.v !== VERSION) return null;
  if (typeof parsed.scenarioId !== 'string' || !/^\d+$/.test(parsed.scenarioId)) return null;
  if (typeof parsed.capturedAt !== 'number' || !Number.isFinite(parsed.capturedAt)) return null;
  const r = parseYomaEntry({ ref: parsed.ref, opportunity: parsed.opportunity, country: parsed.country });
  if (r.status !== 'valid') return null;
  return {
    v: VERSION, scenarioId: parsed.scenarioId, capturedAt: parsed.capturedAt,
    ref: r.value.ref, opportunity: r.value.opportunityId, country: r.value.country,
  };
}

function stripYomaParams(win) {
  try {
    const url = new URL(win.location.href);
    let changed = false;
    for (const k of YOMA_PARAMS) {
      if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; }
    }
    if (!changed) return;
    const next = `${url.pathname}${url.search}${url.hash}`;
    win.history.replaceState(win.history.state, '', next);
  } catch {
    /* never block boot */
  }
}

/** Forget the held context (memory and storage). */
export function clearYomaEntry() {
  held = null;
  storageClear();
}

/**
 * Decide this document load's effect on the held context. Exported for tests.
 * @returns the held snapshot after this load (or null)
 */
export function captureYomaEntry(win = (typeof window !== 'undefined' ? window : undefined), now = Date.now()) {
  if (!win) return null;
  const { present, raw } = readYomaParams(win.location?.search);
  if (!present) return held || normaliseStored(storageRead(win));

  const scenarioMatch = SCENARIO_PATH.exec(win.location?.pathname || '');
  const parsed = parseYomaEntry(raw);
  stripYomaParams(win);

  if (parsed.status !== 'valid' || !scenarioMatch) {
    // A Yoma landing that cannot be trusted: the previous learner's context
    // must not be inherited by whoever follows this link.
    held = null;
    storageClear(win);
    return null;
  }
  held = {
    v: VERSION,
    scenarioId: scenarioMatch[1],
    capturedAt: now,
    ref: parsed.value.ref,
    opportunity: parsed.value.opportunityId,
    country: parsed.value.country,
  };
  storageWrite(held, win);
  return held;
}

/**
 * The `body.yomaEntry` value for a run on `scenarioId`, or null. Null when
 * nothing is held, it has expired (and is then cleared), or it is bound to a
 * different scenario (kept: the learner may come back to it).
 */
export function getYomaEntryFor(scenarioId, now = Date.now()) {
  try {
    const snapshot = held || normaliseStored(storageRead());
    if (!snapshot) return null;
    if (now - snapshot.capturedAt > TTL_MS || snapshot.capturedAt > now + 60 * 1000) {
      clearYomaEntry();
      return null;
    }
    if (String(scenarioId) !== snapshot.scenarioId) return null;
    held = snapshot;
    return { ref: snapshot.ref, opportunity: snapshot.opportunity, country: snapshot.country };
  } catch {
    return null; // the body then omits the field: the run starts unattributed
  }
}

/** Server outcomes after which the held context must be KEPT (the write did not land). */
const KEEP_ON_ATTRIBUTION = new Set(['error', 'timeout']);

/**
 * Put the held context for `body.scenarioId` on a POST /api/runs body as
 * `yomaEntry`. Mutates and returns `body`; adds nothing when no context is held
 * for that scenario (or it expired).
 */
export function attachYomaEntryToBody(body, now = Date.now()) {
  const entry = getYomaEntryFor(body?.scenarioId, now);
  if (entry) body.yomaEntry = entry;
  return body;
}

/**
 * Spend the held context after the run-create response. Cleared only on a 201
 * whose `yomaAttribution` is absent (feature flag off) or is NOT 'error' or
 * 'timeout': when the server failed to save the context the learner keeps it, so
 * the next attempt (a retry, "Practice again") can still carry it.
 * @returns {boolean} true when the context was cleared
 */
export function spendYomaEntryOnResponse(status, data) {
  if (status !== 201) return false;
  const attribution = data && typeof data === 'object' ? data.yomaAttribution : undefined;
  if (typeof attribution === 'string' && KEEP_ON_ATTRIBUTION.has(attribution)) return false;
  clearYomaEntry();
  return true;
}

/** Test seam: forget the in-memory snapshot (not storage). */
export function __resetYomaEntryForTests() {
  held = null;
}

// Boot capture. Imported by AuthContext at app start, so a throw here would
// take the app down over an attribution field: swallow it.
try {
  if (typeof window !== 'undefined') captureYomaEntry(window);
} catch {
  /* never block boot on Yoma entry capture */
}
